import { ERROR_MESSAGES } from '../constants/error-messages';
import { GRAPHQL_POLLING_INTERVAL } from '../constants/general';
import type {
  DownloadManifest,
  DownloadSource,
  DownloadState
} from '../contexts/downloads.context';
import { assertNever } from './exhaustiveness.helper';
import { logError } from './logger.helper';

/**
 * The backing `mod study` job can run up to an hour (`Duration.ofHours(1)` in
 * `ModCli.java`); a saved search's sweep, waited out first, spends the same budget.
 */
export const DOWNLOAD_POLL_TIMEOUT_MS = 65 * 60 * 1000;

/** Only a row written before downloads carried a `source` lacks one. */
export const canStartDownload = (manifest: DownloadManifest): boolean =>
  Boolean(manifest.source);

/**
 * A stable key for "the same download request", used to decide whether a new
 * click supersedes an existing row.
 *
 * File name is not enough: a data table's file name has no run id in it, so the
 * same table from two different recipe runs produces the same name for
 * different data.
 *
 * Excludes `downloadId`, which a resumed job has and a fresh click does not —
 * they are still the same request.
 */
export const downloadSourceIdentity = (source: DownloadSource): string => {
  switch (source.type) {
    case 'STATIC':
      return `STATIC:${source.sourcePath}`;
    case 'DATA_TABLE':
      return `DATA_TABLE:${source.organizationId}:${source.recipeRunId}:${source.dataTable}:${source.format}:${source.group ?? ''}`;
    case 'AUDIT_LOG':
      return `AUDIT_LOG:${source.format}:${source.since ?? ''}:${source.until ?? ''}`;
    default:
      assertNever(source);
      // From a newer deploy: never the same request as anything we can start.
      return JSON.stringify(source);
  }
};

/** `null` while a data table job waits in the server's queue. */
export const getDownloadTimerStart = (
  manifest: DownloadManifest
): number | null => {
  const { source } = manifest;

  if (source?.type === 'DATA_TABLE' && source.downloadId) {
    return source.processingStartTimestamp
      ? // Server clock; clamped so skew can't start the timer in the future.
        Math.min(source.processingStartTimestamp, Date.now())
      : null;
  }

  return manifest.startTimestamp || 0;
};

export const isDownloadInProgress = (state: DownloadState): boolean =>
  state === 'PENDING' || state === 'DOWNLOADING';

/**
 * A job carrying its `source` restores as resumable, so the resolver can ask
 * the server what happened instead of this guessing.
 */
export const rehydrateDownloadState = (
  manifest: DownloadManifest
): DownloadState => {
  if (manifest.state === 'SUCCESS' || manifest.state === 'FAILURE') {
    return manifest.state;
  }

  return canStartDownload(manifest) ? 'PENDING' : 'FAILURE';
};

/**
 * Keeps the first occurrence per `indexDbId ?? id`. Callers pass in-memory rows
 * first, because the IndexedDB copy is a stale snapshot of the same job.
 */
export const dedupeDownloads = (
  downloads: DownloadManifest[]
): DownloadManifest[] => {
  const seen = new Set<string>();

  return downloads.filter((download) => {
    const key = download.indexDbId ?? download.id;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

/** Logs then throws — every terminal failure in a download job's mutation/poll goes through this. */
export const logAndThrow = (message: string): never => {
  const error = new Error(message);
  logError(error);
  throw error;
};

/**
 * Polls until `poll` yields a value. The deadline runs from the click, not from
 * when this loop did, so resuming a stuck job cannot extend it.
 *
 * `poll` returns null to keep waiting and throws to fail. An aborted `signal`
 * rejects it between polls.
 */
export const pollUntilDeadline = async <T>(
  startTimestamp: number,
  poll: () => Promise<T | null>,
  signal?: AbortSignal,
  timedOutMessage = ERROR_MESSAGES.downloads.timedOut.title
): Promise<T> => {
  const deadline = startTimestamp + DOWNLOAD_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    signal?.throwIfAborted();
    const value = await poll();
    signal?.throwIfAborted();

    if (value) {
      return value;
    }

    await new Promise<void>((resolve, reject) => {
      const onAbort = () => {
        clearTimeout(timer);
        reject(signal?.reason);
      };
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, GRAPHQL_POLLING_INTERVAL);
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  signal?.throwIfAborted();
  return logAndThrow(timedOutMessage);
};
